import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface LocationOption {
  id: string;
  name: string;
  type: "hangar" | "ramp";
}

interface BulkActionToolbarProps {
  selectedCount: number;
  onBulkAssign: () => Promise<void>;
  onBulkUnassign: () => Promise<void>;
  onBulkCancel: () => Promise<void>;
  onBulkDelete?: () => Promise<void>;
  onClearSelection: () => void;
  disabled?: boolean;
  canDelete?: boolean;
  locations: LocationOption[];
  selectedLocationId: string;
  onLocationChange: (locationId: string) => void;
}

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  onBulkAssign,
  onBulkUnassign,
  onBulkCancel,
  onBulkDelete,
  onClearSelection,
  disabled = false,
  canDelete = false,
  locations,
  selectedLocationId,
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  const handleBulkAssign = async () => {
    setLoading(true);
    try {
      await onBulkAssign();
    } catch (error) {
      console.error("Bulk assign failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUnassign = async () => {
    setLoading(true);
    try {
      await onBulkUnassign();
    } catch (error) {
      console.error("Bulk unassign failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    setLoading(true);
    try {
      await onBulkCancel();
    } catch (error) {
      console.error("Bulk cancel failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete) return;

    setLoading(true);
    try {
      await onBulkDelete();
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        mb: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" fontWeight={600} color="primary.main">
          {t('widgets.bulkActionToolbar.tripsSelected', { count: selectedCount })}
        </Typography>

        {/* Location Selector */}
        <Select
          size="small"
          value={selectedLocationId}
          onChange={(e) => onLocationChange(e.target.value)}
          displayEmpty
          disabled={disabled || loading}
          sx={{ minWidth: 150, bgcolor: "background.paper" }}
        >
          <MenuItem value="">
            <em>{t('widgets.bulkActionToolbar.selectLocation')}</em>
          </MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name} ({loc.type})
            </MenuItem>
          ))}
        </Select>

        {/* Bulk Assign */}
        <Button
          variant="contained"
          size="small"
          startIcon={
            loading ? <CircularProgress size={16} /> : <PlaceIcon />
          }
          onClick={handleBulkAssign}
          disabled={disabled || loading || !selectedLocationId}
        >
          {t('widgets.bulkActionToolbar.assign')}
        </Button>

        {/* Bulk Unassign */}
        <Button
          variant="outlined"
          size="small"
          startIcon={
            loading ? <CircularProgress size={16} /> : <LocationOffIcon />
          }
          onClick={handleBulkUnassign}
          disabled={disabled || loading}
        >
          {t('widgets.bulkActionToolbar.unassign')}
        </Button>

        {/* Bulk Cancel */}
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          onClick={handleBulkCancel}
          disabled={disabled || loading}
        >
          {t('widgets.bulkActionToolbar.cancel')}
        </Button>

        {/* Bulk Delete */}
        {canDelete && onBulkDelete && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={loading ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
            onClick={handleBulkDelete}
            disabled={disabled || loading}
          >
            {t('widgets.bulkActionToolbar.delete')}
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Clear Selection */}
        <Button
          variant="text"
          size="small"
          startIcon={<ClearIcon />}
          onClick={onClearSelection}
          disabled={disabled || loading}
        >
          {t('widgets.bulkActionToolbar.clear')}
        </Button>
      </Stack>
    </Box>
  );
};
